import type { OpportunityListItem } from '@opportunities/list/-hooks/useOpportunities'
import { useMemo } from 'react'
import type { OpportunityStatus } from '@/lib'
import type { NameResolutionMaps } from '../types'

/**
 * 名前解決用マップを生成するカスタムフック
 *
 * オポチュニティ一覧とステータス一覧から、ID→名前変換用のマップを作成します
 */
export function useNameResolutionMaps(
  opportunities: OpportunityListItem[],
  opportunityStatuses: OpportunityStatus[],
): NameResolutionMaps {
  const customerMap = useMemo(() => {
    const map = new Map<number, string>()
    for (const opp of opportunities) {
      if (opp.customer_id && opp.customer_name) {
        map.set(opp.customer_id, opp.customer_name)
      }
    }
    return map
  }, [opportunities])

  const verticalDivisionMap = useMemo(() => {
    const map = new Map<number, string>()
    for (const opp of opportunities) {
      if (opp.vertical_division_id) {
        const shortName = opp.short_vertical_division_name || opp.vertical_division_name || ''
        map.set(opp.vertical_division_id, shortName)
      }
    }
    return map
  }, [opportunities])

  const lateralDivisionMap = useMemo(() => {
    const map = new Map<number, string>()
    for (const opp of opportunities) {
      if (opp.lateral_division_id) {
        const shortName = opp.short_lateral_division_name || opp.lateral_division_name || ''
        map.set(opp.lateral_division_id, shortName)
      }
    }
    return map
  }, [opportunities])

  const captainMap = useMemo(() => {
    const map = new Map<number, string>()
    for (const opp of opportunities) {
      if (opp.created_by && opp.created_by_name) {
        map.set(opp.created_by, opp.created_by_name)
      }
    }
    return map
  }, [opportunities])

  const salesRecordingGroupMap = useMemo(() => {
    const map = new Map<number, string>()
    for (const opp of opportunities) {
      if (opp.sales_recording_group_id && opp.sales_recording_group_name) {
        map.set(opp.sales_recording_group_id, opp.sales_recording_group_name)
      }
    }
    return map
  }, [opportunities])

  const statusMap = useMemo(() => {
    const map = new Map<number, string>()
    for (const status of opportunityStatuses) {
      map.set(status.id, status.name)
    }
    return map
  }, [opportunityStatuses])

  return {
    customerMap,
    verticalDivisionMap,
    lateralDivisionMap,
    captainMap,
    salesRecordingGroupMap,
    statusMap,
  }
}
